// src/components/orders/SupplierOrdersModal.js
import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Select, Table, message, Spin, Space, Button, DatePicker, Tag, Tooltip } from 'antd';
import {
    SearchOutlined,
    ReloadOutlined,
    CloseCircleOutlined,
    EyeOutlined,
    ClockCircleOutlined,
    CheckCircleOutlined,
    TruckOutlined,
    InboxOutlined,
    PrinterOutlined,
    ExclamationCircleOutlined,
} from '@ant-design/icons';
import { getAllSuppliers } from '../../services/supplierService';
import { getPedidosBySupplier, cancelPedido, getPedidoById, updatePedidoStatus } from '../../services/pedidosService';
import { useAuth } from '../../context/AuthContext';
import OrdersSummaryCards from './OrdersSummaryCards';
import OrderRowActions from './OrderRowActions';
import OrderItemsEditModal from './OrderItemsEditModal';
import OrderPaymentModal from './OrderPaymentModal';
import './SupplierOrdersModal.css';

const { Option } = Select;
const { RangePicker } = DatePicker;

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

/**
 * SupplierOrdersModal Component
 * Modal that shows a summary + orders table for a single supplier.
 * It supports being opened "free" (user picks supplier from the in-modal
 * dropdown) or pre-selected (parent passes `preSelectedSupplierId`).
 *
 * Props:
 * - visible: Boolean — Modal visibility
 * - onClose: Function — Close handler
 * - preSelectedSupplierId: Number|null — Pre-select supplier when opening
 * - onViewOrderDetails: Function(orderId) — View a single order's details
 * - onActionDone: Function (optional) — Callback fired after any mutating
 *   action (cancel / edit items / register payment) so the parent can
 *   refresh its own table.
 */
const SupplierOrdersModal = ({
    visible,
    onClose,
    preSelectedSupplierId = null,
    onViewOrderDetails,
    onActionDone,
}) => {
    const { user } = useAuth();
    const isAdmin = user?.role === 'ClientAdministrator' || user?.role === 'admin';

    const [suppliers, setSuppliers] = useState([]);
    const [loadingSuppliers, setLoadingSuppliers] = useState(false);
    const [selectedSupplierId, setSelectedSupplierId] = useState(null);

    const [supplierData, setSupplierData] = useState(null);
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 20,
        total: 0,
    });

    const [filters, setFilters] = useState({
        status: null,
        dateFrom: null,
        dateTo: null,
    });

    // Client-side payment status filter (not sent to backend)
    const [paymentFilter, setPaymentFilter] = useState(null);

    // Inline status editing state (mirrors ordenesCompraPage behavior)
    const [editingCell, setEditingCell] = useState({ id: null, field: null });
    const [editValue, setEditValue] = useState('');

    // Sub-modal state
    const [editItemsModalVisible, setEditItemsModalVisible] = useState(false);
    const [orderForEdit, setOrderForEdit] = useState(null);
    const [paymentModalVisible, setPaymentModalVisible] = useState(false);
    const [orderForPayment, setOrderForPayment] = useState(null);

    // Load suppliers on mount
    useEffect(() => {
        if (visible) {
            fetchSuppliers();
        }
    }, [visible]);

    // Pre-select supplier if provided
    useEffect(() => {
        if (preSelectedSupplierId && visible) {
            setSelectedSupplierId(preSelectedSupplierId);
            fetchSupplierOrders(preSelectedSupplierId, 1, pagination.pageSize);
        }
    }, [preSelectedSupplierId, visible]);

    // Fetch all suppliers for dropdown
    const fetchSuppliers = async () => {
        setLoadingSuppliers(true);
        try {
            const response = await getAllSuppliers(1, 500, {});
            if (response && response.success) {
                setSuppliers(response.suppliers || []);
            }
        } catch (error) {
            console.error('Error fetching suppliers:', error);
            message.error('Error al cargar proveedores');
        } finally {
            setLoadingSuppliers(false);
        }
    };

    // Fetch orders for selected supplier
    const fetchSupplierOrders = async (supplierId, page = 1, pageSize = 20) => {
        if (!supplierId) {
            message.warning('Por favor seleccione un proveedor');
            return;
        }

        setLoadingOrders(true);
        try {
            const response = await getPedidosBySupplier(supplierId, page, pageSize, filters);

            if (response && response.success) {
                const { supplier, summary, orders: ordersList, pagination: paginationData } = response.data;

                let filteredOrders = ordersList || [];
                if (paymentFilter) {
                    filteredOrders = filteredOrders.filter(o => (o.payment_status || 'PENDIENTE') === paymentFilter);
                }

                setSupplierData({ supplier, summary });
                setOrders(filteredOrders);
                setPagination({
                    current: paginationData?.page || page,
                    pageSize: paginationData?.limit || pageSize,
                    total: paymentFilter ? filteredOrders.length : (paginationData?.total || 0),
                });
            } else {
                message.error('Error al cargar órdenes del proveedor');
            }
        } catch (error) {
            console.error('Error fetching supplier orders:', error);
            if (error.response?.status === 404) {
                message.error('Proveedor no encontrado');
            } else {
                message.error('Error al cargar órdenes del proveedor');
            }
            setSupplierData(null);
            setOrders([]);
        } finally {
            setLoadingOrders(false);
        }
    };

    // Centralized "refresh self + notify parent" handler
    const handleActionDone = useCallback(() => {
        if (selectedSupplierId) {
            fetchSupplierOrders(selectedSupplierId, pagination.current, pagination.pageSize);
        }
        if (onActionDone) {
            onActionDone();
        }
    }, [selectedSupplierId, pagination.current, pagination.pageSize, onActionDone, paymentFilter]);

    // Handle supplier selection change
    const handleSupplierChange = (supplierId) => {
        setSelectedSupplierId(supplierId);
        setSupplierData(null);
        setOrders([]);
        setPagination({ current: 1, pageSize: 20, total: 0 });
        setFilters({ status: null, dateFrom: null, dateTo: null });
        setPaymentFilter(null);

        if (supplierId) {
            fetchSupplierOrders(supplierId, 1, 20);
        }
    };

    // Handle table pagination change
    const handleTableChange = (paginationConfig) => {
        if (selectedSupplierId) {
            fetchSupplierOrders(
                selectedSupplierId,
                paginationConfig.current,
                paginationConfig.pageSize
            );
        }
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    // Apply filters
    const handleApplyFilters = () => {
        if (selectedSupplierId) {
            fetchSupplierOrders(selectedSupplierId, 1, pagination.pageSize);
        }
    };

    // Apply payment filter client-side (no backend roundtrip)
    const handlePaymentFilterChange = (value) => {
        setPaymentFilter(value || null);
        if (selectedSupplierId) {
            fetchSupplierOrders(selectedSupplierId, pagination.current, pagination.pageSize);
        }
    };

    // Clear filters
    const handleClearFilters = () => {
        setFilters({ status: null, dateFrom: null, dateTo: null });
        setPaymentFilter(null);
        if (selectedSupplierId) {
            setTimeout(() => {
                fetchSupplierOrders(selectedSupplierId, 1, pagination.pageSize);
            }, 100);
        }
    };

    // Handle modal close
    const handleClose = () => {
        setSelectedSupplierId(null);
        setSupplierData(null);
        setOrders([]);
        setFilters({ status: null, dateFrom: null, dateTo: null });
        setPaymentFilter(null);
        setPagination({ current: 1, pageSize: 20, total: 0 });
        setEditItemsModalVisible(false);
        setOrderForEdit(null);
        setPaymentModalVisible(false);
        setOrderForPayment(null);
        onClose();
    };

    // Action: view
    const handleViewOrder = (record) => {
        if (onViewOrderDetails) onViewOrderDetails(record.id);
    };

    // Action: edit items
    const handleEditOrderItems = async (record) => {
        try {
            const response = await getPedidoById(record.id);
            if (response && response.success && response.data) {
                const orderData = response.data.order || response.data;
                const items = response.data.items || orderData.items || [];
                setOrderForEdit({ ...orderData, items });
                setEditItemsModalVisible(true);
            } else {
                message.error('Error al cargar detalles de la orden para edición');
            }
        } catch (error) {
            console.error('Error fetching order for edit:', error);
            message.error('Error al cargar la orden');
        }
    };

    const handleEditItemsSaved = () => {
        setEditItemsModalVisible(false);
        setOrderForEdit(null);
        handleActionDone();
    };

    // Action: cancel
    const handleCancelOrder = (record) => {
        Modal.confirm({
            title: '¿Cancelar Orden?',
            icon: <ExclamationCircleOutlined />,
            content: `¿Desea cancelar la orden de compra "${record.order_number}"? Esta acción no se puede deshacer.`,
            okText: 'Sí, cancelar orden',
            okType: 'danger',
            cancelText: 'No',
            onOk: async () => {
                try {
                    const response = await cancelPedido(record.id, 'Cancelado por el usuario');
                    if (response.success) {
                        message.success('Orden de compra cancelada exitosamente');
                        handleActionDone();
                    }
                } catch (error) {
                    console.error('Error cancelling order:', error);
                    message.error(error.response?.data?.message || 'Error al cancelar la orden de compra');
                }
            },
        });
    };

    // Action: register payment
    const handleRegisterPayment = (record) => {
        setOrderForPayment(record);
        setPaymentModalVisible(true);
    };

    const handlePaymentRegistered = () => {
        setPaymentModalVisible(false);
        setOrderForPayment(null);
        handleActionDone();
    };

    // Inline status cell edit (mirrors ordenesCompraPage.handleCellEdit/Save)
    const handleStatusCellEdit = (record) => {
        setEditingCell({ id: record.id, field: 'status' });
        setEditValue(record.status || '');
    };

    const handleStatusCellSave = async (record) => {
        if (editValue === record.status) {
            setEditingCell({ id: null, field: null });
            return;
        }

        if (editValue === 'CANCELADO') {
            setEditingCell({ id: null, field: null });
            Modal.confirm({
                title: '¿Cancelar Orden?',
                icon: <ExclamationCircleOutlined />,
                content: `¿Desea cancelar la orden de compra "${record.order_number}"? Esta acción no se puede deshacer.`,
                okText: 'Sí, cancelar orden',
                okType: 'danger',
                cancelText: 'No',
                onOk: async () => {
                    try {
                        const response = await cancelPedido(record.id, 'Cancelado por el usuario');
                        if (response.success) {
                            message.success('Orden de compra cancelada exitosamente');
                            handleActionDone();
                        }
                    } catch (error) {
                        console.error('Error cancelling order:', error);
                        message.error(error.response?.data?.message || 'Error al cancelar la orden de compra');
                    }
                },
            });
            return;
        }

        try {
            const response = await updatePedidoStatus(record.id, editValue);
            if (response.success) {
                if (editValue === 'RECIBIDO' && response.data?.order) {
                    const order = response.data.order;
                    const stockCount = order.stock_updated_count || 0;
                    const cajaCreated = order.caja_movement_created === 1;
                    const msgParts = ['Orden recibida.'];
                    if (stockCount > 0) {
                        msgParts.push(`Stock +${stockCount} producto${stockCount > 1 ? 's' : ''}.`);
                    }
                    if (cajaCreated) {
                        msgParts.push('Egreso registrado en Caja.');
                    }
                    message.success(msgParts.join(' '));
                } else {
                    message.success('Orden actualizada exitosamente');
                }
                handleActionDone();
            }
        } catch (error) {
            console.error('Error updating order status:', error);
            message.error(error.response?.data?.message || 'Error al actualizar el estado de la orden');
        } finally {
            setEditingCell({ id: null, field: null });
        }
    };

    const getStatusColor = (status) => {
        const colors = {
            PENDIENTE: 'orange',
            APROBADO: 'blue',
            EN_TRANSITO: 'cyan',
            PARCIAL: 'purple',
            RECIBIDO: 'green',
            CANCELADO: 'red',
        };
        return colors[status] || 'default';
    };

    // Status helpers
    const canEditOrder = (status) => {
        return status === 'PENDIENTE'
            || status === 'APROBADO'
            || status === 'RECIBIDO'
            || status === 'EN_TRANSITO'
            || status === 'PARCIAL';
    };

    // Payment status badge helpers (duplicated inline — see ordenesCompraPage.js)
    const getPaymentStatusConfig = (pStatus) => {
        const config = {
            'PENDIENTE': { label: 'Sin pagar', color: 'default'  },
            'PARCIAL':   { label: 'Parcial',   color: 'purple'   },
            'PAGADO':    { label: 'Pagado',    color: 'green'    },
        };
        return config[pStatus] || { label: pStatus || '-', color: 'default' };
    };

    const getStatusLabel = (status) => {
        const labels = {
            'PENDIENTE': 'Pendiente',
            'APROBADO': 'Aprobado',
            'EN_TRANSITO': 'En Tránsito',
            'PARCIAL': 'Parcial',
            'RECIBIDO': 'Recibido',
            'CANCELADO': 'Cancelado',
        };
        return labels[status] || status;
    };

    // Get status tag
    const getStatusTag = (status) => {
        const statusConfig = {
            PENDIENTE: { color: 'orange', icon: <ClockCircleOutlined /> },
            APROBADO: { color: 'blue', icon: <CheckCircleOutlined /> },
            EN_TRANSITO: { color: 'cyan', icon: <TruckOutlined /> },
            PARCIAL: { color: 'purple', icon: <InboxOutlined /> },
            RECIBIDO: { color: 'green', icon: <CheckCircleOutlined /> },
            CANCELADO: { color: 'red', icon: <CloseCircleOutlined /> },
        };
        const config = statusConfig[status] || { color: 'default', icon: null };
        return (
            <Tag color={config.color} icon={config.icon}>
                {getStatusLabel(status)}
            </Tag>
        );
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('es-AR', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        });
    };

    // Format currency
    const formatPrice = (price) => {
        if (typeof price !== 'number' || isNaN(price)) return '$ 0.00';
        return `$ ${price.toLocaleString('es-AR', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };

    // Table columns
    const columns = [
        {
            title: 'N° Orden',
            dataIndex: 'order_number',
            key: 'order_number',
            width: 130,
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: 'Factura Prov.',
            dataIndex: 'related_supplier_invoice',
            key: 'related_supplier_invoice',
            width: 140,
            render: (text) => text || '-',
        },
        {
            title: 'Fecha',
            dataIndex: 'order_date',
            key: 'order_date',
            width: 110,
            render: (date) => formatDate(date),
        },
        {
            title: 'Envío',
            dataIndex: 'status',
            key: 'status',
            width: 115,
            render: (status, record) => {
                const isEditing = editingCell.id === record.id && editingCell.field === 'status';
                const isDisabled = status === 'CANCELADO' || status === 'RECIBIDO';

                if (isEditing) {
                    return (
                        <Select
                            value={editValue}
                            onChange={setEditValue}
                            onBlur={() => handleStatusCellSave(record)}
                            onPressEnter={() => handleStatusCellSave(record)}
                            style={{ width: '100%' }}
                            size="small"
                            autoFocus
                        >
                            <Option value="PENDIENTE">Pendiente</Option>
                            <Option value="APROBADO">Aprobado</Option>
                            <Option value="EN_TRANSITO">En Tránsito</Option>
                            <Option value="RECIBIDO">Recibido</Option>
                            <Option value="CANCELADO">Cancelado</Option>
                        </Select>
                    );
                }

                return (
                    <Tag
                        color={getStatusColor(status)}
                        onClick={() => !isDisabled && isAdmin && handleStatusCellEdit(record)}
                        style={{
                            cursor: isDisabled || !isAdmin ? 'not-allowed' : 'pointer',
                            opacity: isDisabled || !isAdmin ? 0.6 : 1,
                            margin: 0,
                            minWidth: 95,
                            textAlign: 'center',
                        }}
                        title={isDisabled
                            ? 'No se puede modificar'
                            : !isAdmin
                                ? 'Requiere rol ClientAdministrator'
                                : 'Haz clic para editar'}
                    >
                        {getStatusLabel(status)}
                    </Tag>
                );
            },
        },
        {
            title: 'Pago',
            key: 'payment_status',
            width: 100,
            render: (_, record) => {
                const { status } = record;
                const { label: payLabel, color: payColor } = getPaymentStatusConfig(record.payment_status);
                const paid = Number(record.paid_amount) || 0;
                const paidTitle = paid > 0
                    ? `Pagado: $ ${paid.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                    : undefined;

                return (
                    <Tooltip title={paidTitle || (status !== 'CANCELADO' ? 'Registrar pago' : undefined)}>
                        <Tag
                            color={payColor}
                            style={{ margin: 0, minWidth: 80, textAlign: 'center', cursor: status !== 'CANCELADO' ? 'pointer' : 'default' }}
                            onClick={() => {
                                if (status === 'CANCELADO') return;
                                handleRegisterPayment(record);
                            }}
                        >
                            {payLabel}
                        </Tag>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Total',
            dataIndex: 'total',
            key: 'total',
            width: 120,
            align: 'right',
            render: (price) => formatPrice(price),
        },
        {
            title: 'IVA',
            dataIndex: 'iva',
            key: 'iva',
            width: 100,
            align: 'right',
            render: (price) => formatPrice(price),
        },
        {
            title: 'Descuento',
            dataIndex: 'discount',
            key: 'discount',
            width: 100,
            align: 'right',
            render: (price) => formatPrice(price),
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 165,
            fixed: 'right',
            render: (_, record) => (
                <OrderRowActions
                    order={record}
                    isAdmin={isAdmin}
                    canEditOrder={canEditOrder}
                    getStatusLabel={getStatusLabel}
                    onView={handleViewOrder}
                    onEdit={handleEditOrderItems}
                    onCancel={handleCancelOrder}
                    onRegisterPayment={handleRegisterPayment}
                />
            ),
        },
    ];

    return (
        <>
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingRight: '40px' }}>
                        <span>Órdenes por Proveedor</span>
                        {selectedSupplierId && (
                            <Button
                                type="primary"
                                icon={<PrinterOutlined />}
                                onClick={() => {
                                    window.open(`#/print-po-supplier?supplierId=${selectedSupplierId}`, '_blank');
                                }}
                                size="middle"
                            >
                                Imprimir
                            </Button>
                        )}
                    </div>
                }
                open={visible}
                onCancel={handleClose}
                width={1200}
                footer={[
                    <Button key="close" onClick={handleClose}>
                        Cerrar
                    </Button>
                ]}
                className="supplier-orders-modal"
            >
                <div className="modal-content">
                    {/* Supplier Selector */}
                    <div className="supplier-selector-section">
                        <label>Seleccione un proveedor:</label>
                        <Select
                            showSearch
                            placeholder="Buscar y seleccionar proveedor"
                            style={{ width: '100%' }}
                            value={selectedSupplierId}
                            onChange={handleSupplierChange}
                            loading={loadingSuppliers}
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                            }
                        >
                            {suppliers.map(supplier => (
                                <Option key={supplier.id} value={supplier.id}>
                                    {supplier.razon_social} - CUIT: {supplier.documento_nro}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    {/* Show loading spinner or data */}
                    {loadingOrders && !supplierData ? (
                        <div className="loading-container">
                            <Spin size="large" tip="Cargando órdenes del proveedor..." />
                        </div>
                    ) : supplierData ? (
                        <>
                            {/* Summary Cards — adapt flat supplier summary into the { overall, byStatus } shape
                                 that OrdersSummaryCards expects (same as getAllOrders response) */}
                            <OrdersSummaryCards
                                summary={supplierData.summary ? {
                                    overall: {
                                        all:           supplierData.summary.all           || { total: 0, iva: 0, count: 0 },
                                        paid_total:    supplierData.summary.paid_total    || 0,
                                        iva_paid_all:  supplierData.summary.iva_paid_all  || 0,
                                        pending_total: supplierData.summary.pending_total || 0,
                                        monthlyBreakdown: [], // not available per-supplier; hides month selector
                                    },
                                    byStatus: supplierData.summary.byStatus || {},
                                } : null}
                                supplierName={supplierData.supplier?.supplier_razon_social}
                                orders={orders}
                            />

                            {/* Filters */}
                            <div className="supplier-orders-filters">
                                <Space wrap size="middle">
                                    <Select
                                        placeholder="Filtrar por envío"
                                        style={{ width: 180 }}
                                        allowClear
                                        value={filters.status}
                                        onChange={(value) => handleFilterChange('status', value)}
                                    >
                                        <Option value="PENDIENTE">Pendiente</Option>
                                        <Option value="APROBADO">Aprobado</Option>
                                        <Option value="EN_TRANSITO">En Tránsito</Option>
                                        <Option value="PARCIAL">Parcial</Option>
                                        <Option value="RECIBIDO">Recibido</Option>
                                        <Option value="CANCELADO">Cancelado</Option>
                                    </Select>

                                    <Select
                                        placeholder="Filtrar por pago"
                                        style={{ width: 160 }}
                                        allowClear
                                        value={paymentFilter}
                                        onChange={handlePaymentFilterChange}
                                    >
                                        <Option value="PENDIENTE">Sin pagar</Option>
                                        <Option value="PARCIAL">Parcial</Option>
                                        <Option value="PAGADO">Pagado</Option>
                                    </Select>

                                    <RangePicker
                                        format="DD/MM/YYYY"
                                        placeholder={['Desde', 'Hasta']}
                                        onChange={(dates) => {
                                            if (dates) {
                                                handleFilterChange('dateFrom', dates[0].format('YYYY-MM-DD'));
                                                handleFilterChange('dateTo', dates[1].format('YYYY-MM-DD'));
                                            } else {
                                                handleFilterChange('dateFrom', null);
                                                handleFilterChange('dateTo', null);
                                            }
                                        }}
                                    />

                                    <Button
                                        type="primary"
                                        icon={<SearchOutlined />}
                                        onClick={handleApplyFilters}
                                    >
                                        Aplicar
                                    </Button>
                                    <Button
                                        icon={<ReloadOutlined />}
                                        onClick={handleClearFilters}
                                    >
                                        Limpiar
                                    </Button>
                                </Space>
                            </div>

                            {/* Orders Table */}
                            <div className="supplier-orders-table">
                                <Table
                                    columns={columns}
                                    dataSource={orders}
                                    rowKey="id"
                                    loading={loadingOrders}
                                    pagination={pagination}
                                    onChange={handleTableChange}
                                    size="middle"
                                    scroll={{ x: 1100 }}
                                />
                            </div>
                        </>
                    ) : selectedSupplierId ? (
                        <div className="empty-state">
                            <p>No se encontraron órdenes para este proveedor</p>
                        </div>
                    ) : (
                        <div className="empty-state">
                            <p>Seleccione un proveedor para ver sus órdenes</p>
                        </div>
                    )}
                </div>
            </Modal>

            {/* Edit Items (nested) */}
            <OrderItemsEditModal
                visible={editItemsModalVisible}
                onClose={() => {
                    setEditItemsModalVisible(false);
                    setOrderForEdit(null);
                }}
                onSaved={handleEditItemsSaved}
                order={orderForEdit}
                userRole={user?.role}
            />

            {/* Payment Registration (nested) */}
            <OrderPaymentModal
                visible={paymentModalVisible}
                order={orderForPayment}
                onClose={() => {
                    setPaymentModalVisible(false);
                    setOrderForPayment(null);
                }}
                onPaymentRegistered={handlePaymentRegistered}
            />
        </>
    );
};

export default SupplierOrdersModal;
